import { createContext, useState, useEffect } from 'react'
import { userAuthService } from '../services/user-auth.service.js'

/**
 *  The UserManager contains the action methods formerly on the User class.
 *   Where previously we would perform some action of the global user
 *   object (eg. user.authenticateUserPass()), now we invoke a method
 *   on the UserManager (eg. userManager.authenticateUserPass())
 *  Mostly these pass through to the userAuthService and update the
 *   current user. They mimic the behavior of the former User object
 *   by throwing errors expected by calling code.
 */
class UserManager {
  constructor () {
    this._fnSetUser = null
    this._tokenTimer = null
  }

  getAuthProviders () {
    return userAuthService.getAuthProviders()
  }

  getDefaultProvider () {
    return userAuthService.getDefaultProvider()
  }

  getDefaultLoginUrl () {
    return userAuthService.getDefaultLoginUrl()
  }

  setUserFunction (fnSetUser) {
    this._fnSetUser = fnSetUser
  }

  async checkServerIfAuthenticated () {
    const user = await userAuthService.checkServerIfAuthenticated()
    if (!_currentUser.isAuthenticated() && !user.isAuthenticated()) {
      // If the current user is unauthenticated, and the auth check returns unauthenticated, do
      //  not update the user object. (This will preserve an unauthenticated user with a redirect
      //  property set)
      return
    }
    this.updateCurrentUser(user)
  }

  async authenticateToken (act, reportid) {
    const user = await userAuthService.authenticateToken(act, reportid)
    this.updateCurrentUser(user)
    if (user.authError) {
      throw user.authError
    }
  }

  async authenticateOAuth2 (authCode, state) {
    const user = await userAuthService.authenticateOAuth2(authCode, state)
    this.updateCurrentUser(user)
    if (user.authError) {
      throw user.authError
    }
  }

  async authenticateUserPass (
    username,
    password,
    { newPassword, firstName, lastName } = {}
  ) {
    const user = await userAuthService.authenticateUserPass(
      username,
      password,
      { newPassword, firstName, lastName }
    )
    this.updateCurrentUser(user)
    // Simply return the user instead of throwing an error so profile update can be handled
    return user
  }

  resetUser (authError) {
    const user = userAuthService.resetUser(authError)
    this.updateCurrentUser(user)
  }

  async signout () {
    const user = await userAuthService.signout()
    this.updateCurrentUser(user)
  }

  resetPassword (username) {
    return userAuthService.resetPassword(username)
  }

  updatePassword (username, password, code) {
    return userAuthService.updatePassword(username, password, code)
  }

  updateCurrentUser (user) {
    // Update the global active user and restart any
    //  checks against token expiration
    _currentUser = user
    if (this._fnSetUser) {
      this._fnSetUser(user)
    }

    // Clear any existing token timer
    if (this._tokenTimer) {
      clearTimeout(this._tokenTimer)
      this._tokenTimer = null
    }

    // Install a timer which attempts to retrieve a new user object
    //  from the API after the supplied access token has expired.
    if (user.isAuthenticated() && user.tokenExpiration > 0) {
      const timeRemaining = user.tokenExpiration * 1000 - Date.now()

      if (timeRemaining <= 0) {
        // Wait a second and try again
        setTimeout(() => this.checkServerIfAuthenticated(), 1000)
      } else {
        this._tokenTimer = setTimeout(() => {
          this.checkServerIfAuthenticated()
          this._tokenTimer = null
        }, timeRemaining + 2000)
      }
    }
  }
}

// This global holds the current user object
let _currentUser = userAuthService.resetUser()

// This is used by global (non-React) methods which need to access the user object
// Any resulting action would be initiated by invoking methods on the userManager
export const currentUser = () => _currentUser

export const UserContext = createContext()
export const userManager = new UserManager()

// This custom context provider holds the current user in its
//  state. Updates to the user object as a result of sign-in,
//  sign-out, or token refresh update the context's state so
//  changes are propagated to its consumers
export const UserContextProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(_currentUser)
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    userManager.setUserFunction(setCurrentUser)
    setIsReady(true)

    return () => {
      userManager.setUserFunction(null)
      setIsReady(false)
    }
  }, [])

  return isReady ? (<UserContext.Provider value={currentUser}>{children}</UserContext.Provider>) : (
    ''
  )
}
